<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  contentAttributes,
  sourceId,
  groupSenderHeader,
} = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  // Outgoing agent bubble uses WhatsApp's outgoing green. Theme-aware via an
  // explicit `body.dark` rule in <style> (the dashboard toggles dark mode by
  // adding `.dark` to <body>): bright mint in day mode, deep green at night.
  [MESSAGE_VARIANTS.AGENT]: 'wa-agent-bubble',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

// Shopee direct reply override — orange bg, white text
const isShopeeDirectReply = computed(() => {
  const attrs = contentAttributes?.value ?? contentAttributes ?? {};
  return attrs.shopee_direct === true || attrs.shopeeDirect === true;
});

// WhatsApp marks forwarded messages; Evolution passes contextInfo.isForwarded
// into content_attributes.forwarded. Show a small italic "Forwarded" label at the
// top of the bubble (WhatsApp-style) so CS can tell a customer relayed someone
// else's message (e.g. a payment receipt) rather than sent their own.
const isForwarded = computed(() => {
  const attrs = contentAttributes?.value ?? contentAttributes ?? {};
  return attrs.forwarded === true;
});

// WhatsApp "replied elsewhere" override — green bg, white text.
// A reply sent from the WhatsApp phone/app (not from omnichat) is synced back by
// Evolution with the native WhatsApp message id, whereas omnichat/API/AI sends are
// relayed by Baileys with a 'WAID:3EB0…' id. So: outgoing + WAID prefix + not 3EB0.
// Gated to outgoing (ORIENTATION.RIGHT) because incoming customer messages also
// carry non-3EB0 WAID ids. 'WAID:' only ever appears on WhatsApp inboxes.
const isWaDirectReply = computed(() => {
  if (orientation.value !== ORIENTATION.RIGHT) return false;
  // Guard: when sourceId.value is nullish the `?? sourceId` fallback yields the
  // ref OBJECT (not a string), so .startsWith threw "is not a function" and
  // crashed the whole outgoing bubble render (blank AI/agent messages, 2026-06-16).
  const sid = sourceId?.value ?? sourceId;
  return (
    typeof sid === 'string' &&
    sid.startsWith('WAID:') &&
    !sid.startsWith('WAID:3EB0')
  );
});

const messageClass = computed(() => {
  if (isShopeeDirectReply.value) {
    return ['shopee-direct-bubble', orientationMap[orientation.value]];
  }

  if (isWaDirectReply.value) {
    return ['wa-direct-bubble', orientationMap[orientation.value]];
  }

  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const openGroupSender = () => {
  const target = groupSenderHeader.value?.linkTarget;
  if (target?.kind === 'wa') {
    emitter.emit(BUS_EVENTS.OPEN_WA_MENTION, target.phone);
  }
};

// Show the timestamp on EVERY message (WhatsApp-style), including grouped ones —
// operators rely on per-message times for screenshots.
const shouldShowMeta = computed(
  () => !props.hideMeta && variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const metaTextClass = computed(() => {
  if (isShopeeDirectReply.value) return 'text-white/60';
  if (isWaDirectReply.value) return 'text-white/60';
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return 'text-n-amber-12/50';
  return 'text-n-slate-11';
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// WhatsApp-style reply chip: quoting a photo shows a small thumbnail beside
// the caption/placeholder text instead of the text-only "Picture message".
const replyToImageUrl = computed(() => {
  const attachments = inReplyTo?.value?.attachments;
  if (!attachments?.length) return null;

  const first = attachments[0];
  const fileType = first.fileType ?? first.file_type;
  if (fileType !== 'image') return null;

  return (
    first.thumbUrl ?? first.thumb_url ?? first.dataUrl ?? first.data_url ?? null
  );
});
</script>

<template>
  <div
    class="text-sm group"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div v-if="groupSenderHeader" class="min-w-0 mb-1 text-xs font-medium">
      <button
        v-if="groupSenderHeader.linkTarget"
        type="button"
        class="block max-w-full truncate cursor-pointer text-n-blue-text hover:underline ltr:text-left rtl:text-right"
        @click="openGroupSender"
      >
        {{ groupSenderHeader.displayName }}
      </button>
      <span v-else class="block truncate text-n-slate-11">
        {{ groupSenderHeader.displayName }}
      </span>
    </div>
    <div
      v-if="isForwarded"
      class="flex items-center gap-1 mb-1 text-xs font-medium opacity-70"
    >
      <span class="i-lucide-forward size-3.5" />
      {{ $t('CONVERSATION.FORWARDED') }}
    </div>
    <div
      v-if="inReplyTo"
      class="flex items-center gap-2 p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="flex-1 min-w-0 prose prose-bubble line-clamp-2"
      />
      <img
        v-if="replyToImageUrl"
        :src="replyToImageUrl"
        class="object-cover rounded-md size-10 shrink-0"
        alt=""
      />
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        metaTextClass,
      ]"
      class="justify-end mt-0.5 -mb-1"
    />
  </div>
</template>

<style>
.shopee-direct-bubble {
  background: #ea580c;
  color: #fff;
}
.shopee-direct-bubble .prose {
  color: #fff;
}
.shopee-direct-bubble a {
  color: #fed7aa;
}
.wa-direct-bubble {
  background: #16a34a;
  color: #fff;
}
.wa-direct-bubble .prose {
  color: #fff;
}
.wa-direct-bubble a {
  color: #bbf7d0;
}

/* Outgoing agent bubble — WhatsApp green, theme-aware via the dashboard's
   `body.dark` toggle. Day: bright mint + dark text; night: deep green + white. */
.wa-agent-bubble {
  background-color: #d9fdd3;
  color: #0f172a;
}
body.dark .wa-agent-bubble {
  background-color: #005c4b;
  color: #ffffff;
}

/* WhatsApp-style inline timestamp for plain text bubbles: reserve trailing space
   on the last line (::after spacer) and pin the time + status to the bottom-right,
   so short messages keep the time on the same line and long ones wrap above it. */
.wa-inline-meta {
  position: relative;
}
.wa-inline-meta .prose-bubble > :last-child::after {
  content: '';
  display: inline-block;
  width: 3.5rem;
  height: 1px;
}
.wa-inline-meta .message-meta {
  position: absolute;
  right: 0.75rem;
  bottom: 0.375rem;
  margin: 0;
}
</style>
